import React, { useEffect, useMemo, useState } from 'react';
import { sparkToast } from '@spark';
import OverallScoresRow from './OverallScoresRow';
import LLMJudgeBreakdownChart from './LLMJudgeBreakdownChart';
import ClassicMetricsChart from './ClassicMetricsChart';
import ContentGenerationAccuracy from './ContentGenerationAccuracy';
import LLMJudgeConfigModal from './LLMJudgeConfigModal';

import ClassicMetricsConfigModal from './ClassicMetricsConfigModal';
import { CLASSIC_METRICS_DEFAULTS, isClassicMetricVisible } from './classicMetricsDefaults';
import {
  extractContentGenerationMetrics,
  hasContentGenerationMetrics,
  isContentGenerationMetricKey,
} from './contentGenerationMetrics';
import { buildBuiltinCriteriaItems, judgeConfigToUsecaseUpdatePayload, usecaseToJudgeConfig } from './llmJudgeConfigUtils';
import { updateLlmJudgeConfig } from '@/features/evaluation/api/summarizationApi';
import { TaskType } from '@/shared/domain/taskTypes';

const SummarizationOverview = ({
  evaluationResult,
  loading = false,
  showComparison = true,
  usecase,
  onPersistJudgeConfig,
  isFileComparison = false,
  taskType = TaskType.summarization,
}) => {
  const overview = evaluationResult?.all_metrics?.summarization_overview;
  const cards = overview?.score_cards || [];
  const [judgeModalOpen, setJudgeModalOpen] = useState(false);
  const [classicModalOpen, setClassicModalOpen] = useState(false);
  const [accuracyModalOpen, setAccuracyModalOpen] = useState(false);
  const [judgeConfig, setJudgeConfig] = useState(() => usecaseToJudgeConfig(usecase));
  const [classicConfig, setClassicConfig] = useState(() => ({
    ...CLASSIC_METRICS_DEFAULTS,
    ...(usecase?.classic_metrics_config || {}),
  }));

  useEffect(() => {
    // Keep UI in sync with server-side usecase config.
    setJudgeConfig(usecaseToJudgeConfig(usecase));
    setClassicConfig({ ...CLASSIC_METRICS_DEFAULTS, ...(usecase?.classic_metrics_config || {}) });
  }, [usecase]);

  const persistClassicConfig = async (next) => {
    setClassicConfig(next);
    try {
      await updateLlmJudgeConfig({
        client_id: usecase?.client_id,
        usecase_id: usecase?._id,
        classic_metrics_config: next,
      });
    } catch (error) {
      console.error('Failed to save classic metrics configuration:', error);
      sparkToast.error('Failed to save classic metrics configuration');
    }
  };

  const mandatoryCriteriaPrompts = usecase?.mandatory_criteria_prompts || {};
  const builtinItems = useMemo(
    () => buildBuiltinCriteriaItems(mandatoryCriteriaPrompts, judgeConfig?.enabledById),
    [mandatoryCriteriaPrompts, judgeConfig?.enabledById],
  );

  const teacherCard = useMemo(() => cards.find(c => c?.type === 'teacher'), [cards]);
  const studentCard = useMemo(() => cards.find(c => c?.type === 'student') || cards?.[1] || cards?.[0], [cards]);
  const groundTruthCard = useMemo(() => cards.find(c => c?.type === 'ground_truth'), [cards]);

  // The usecase-level judge config only describes the *latest* run. Historical
  // versions may have been evaluated with different criteria (e.g. v1 ran with
  // Coherence before it was disabled for v2), so only apply the enabled-by-id
  // visibility filter when viewing the latest version; for older versions show
  // whatever criteria actually have data.
  const isViewingLatestVersion = useMemo(() => {
    const versions = (evaluationResult?.versions || []).map(Number).filter(Number.isFinite);
    const selected = Number(evaluationResult?.selected_version);
    if (versions.length === 0 || !Number.isFinite(selected)) return true;
    return Math.floor(selected) >= Math.floor(Math.max(...versions));
  }, [evaluationResult?.versions, evaluationResult?.selected_version]);

  const llmJudgeData = useMemo(() => {
    const d = overview?.llm_judge_breakdown;
    if (!d?.metrics) return d;
    const enabled = judgeConfig?.enabledById || {};
    const isFiniteNumber = (v) => {
      if (v === null || v === undefined) return false;
      if (typeof v === 'string' && v.trim() === '') return false;
      const n = typeof v === 'number' ? v : Number(v);
      return Number.isFinite(n);
    };
    return {
      ...d,
      metrics: d.metrics
        .filter(m => !isViewingLatestVersion || (enabled[m.key] !== false && enabled[`custom_${m.key}`] !== false))
        .filter(m => isFiniteNumber(m.teacher) || isFiniteNumber(m.student) || isFiniteNumber(m.ground_truth)),
    };
  }, [overview?.llm_judge_breakdown, judgeConfig, isViewingLatestVersion]);

  const classicMetricsData = useMemo(() => {
    const d = overview?.classic_metrics;
    if (!d?.metrics) return d;

    const cfg = classicConfig || CLASSIC_METRICS_DEFAULTS;

    return {
      ...d,
      // Keep unknown/server-specific metrics always visible (e.g. SHANI-SCORE),
      // but apply toggles for known classic metrics. Content-generation
      // accuracy metrics (Exact Match, MinHash, Dict Match) never render in
      // this chart: they move to the dedicated accuracy section for content
      // generation tasks and stay hidden for summarization tasks.
      metrics: d.metrics.filter(
        (m) => !isContentGenerationMetricKey(m.key) && isClassicMetricVisible(m.key, cfg),
      ),
    };
  }, [overview?.classic_metrics, classicConfig]);

  const isContentGenerationTask = taskType === TaskType.contentGeneration;

  const contentGenerationData = useMemo(
    () => extractContentGenerationMetrics(overview?.classic_metrics?.metrics, classicConfig),
    [overview?.classic_metrics, classicConfig],
  );

  // Gate the card on the *unfiltered* metrics (defaults = all enabled): when
  // the user disables every accuracy toggle, the card must stay visible (with
  // its empty state) so Configure remains reachable to re-enable them.
  const contentGenerationDataUnfiltered = useMemo(
    () => extractContentGenerationMetrics(overview?.classic_metrics?.metrics, CLASSIC_METRICS_DEFAULTS),
    [overview?.classic_metrics],
  );

  const showContentGenerationSection =
    isContentGenerationTask && (loading || hasContentGenerationMetrics(contentGenerationDataUnfiltered));

  const classicModelLabels = overview?.classic_metrics?.models;
  const contentGenTeacherLabel =
    classicModelLabels?.find?.((m) => m?.key === 'teacher')?.label || 'Teacher';
  const contentGenStudentLabel =
    classicModelLabels?.find?.((m) => m?.key === 'student')?.label || 'Specific AI';

  const judgeHasAnyEnabledCriteria = useMemo(() => {
    // If there are enabled builtins (and/or mandatory prompts), we expect judge data to arrive.
    // This helps us decide between "loading" and "no data" when the backend hasn't populated metrics yet.
    return Array.isArray(builtinItems) && builtinItems.length > 0;
  }, [builtinItems]);

  const judgeHasData = useMemo(() => {
    const t = teacherCard?.score;
    const s = studentCard?.score;
    const hasScore =
      (t !== null && t !== undefined && t !== '') ||
      (s !== null && s !== undefined && s !== '');
    const hasBreakdown = (llmJudgeData?.metrics || []).length > 0;
    return hasScore || hasBreakdown;
  }, [teacherCard?.score, studentCard?.score, llmJudgeData?.metrics]);

  const judgeLoading = !loading && judgeHasAnyEnabledCriteria && !judgeHasData;

  const teacherJudgeProgress = evaluationResult?.teacher_model_judge_progress;
  const teacherModelProcessing = evaluationResult?.teacher_model_processing || false;
  const teacherModelFailed = evaluationResult?.teacher_model_failed || false;
  const teacherModelError = evaluationResult?.teacher_model_error || null;
  const teacherModelErrorDetails = evaluationResult?.teacher_model_error_details || null;
  const teacherErrorText = useMemo(() => {
    const detailsException = typeof teacherModelErrorDetails?.exception === 'string'
      ? teacherModelErrorDetails.exception
      : null;
    const detailsMessage = typeof teacherModelErrorDetails?.message === 'string'
      ? teacherModelErrorDetails.message
      : null;
    const raw = [teacherModelError, detailsException, detailsMessage]
      .filter(v => typeof v === 'string' && v.trim())
      .join(' ');
    const normalized = raw.toLowerCase();

    const isQuotaError =
      normalized.includes('billing_hard_limit_reached') ||
      normalized.includes('billing hard limit has been reached') ||
      normalized.includes('insufficient_quota') ||
      normalized.includes('exceeded your current quota');

    if (isQuotaError) {
      return 'Teacher evaluation stopped because the provider billing quota was reached. Please add credits or update billing, then rerun evaluation.';
    }

    if (detailsException || teacherModelError) {
      return `Teacher evaluation stopped: ${detailsException || teacherModelError}`;
    }
    return teacherModelFailed
      ? 'Teacher evaluation stopped due to an upstream provider error. Please rerun evaluation.'
      : null;
  }, [teacherModelError, teacherModelErrorDetails, teacherModelFailed]);
  const teacherHasError = teacherModelFailed || Boolean(teacherErrorText);
  const hasTeacherScore =
    teacherCard != null &&
    teacherCard?.score != null &&
    teacherCard?.score !== '';
  const teacherCardInProgress =
    showComparison &&
    (teacherCard != null) &&
    (teacherCard?.score == null || teacherCard?.score === '') &&
    teacherJudgeProgress &&
    Number(teacherJudgeProgress.total) > 0 &&
    (Number(teacherJudgeProgress.completed || 0) + Number(teacherJudgeProgress.failed || 0)) < Number(teacherJudgeProgress.total);
  // When student/benchmark data exists but teacher hasn't arrived yet, keep
  // loading so the card never flashes "No data" during the gap between
  // evaluation completing and teacher LLM judgment starting.
  const teacherCardMissingButExpected =
    showComparison &&
    !teacherHasError &&
    judgeHasAnyEnabledCriteria &&
    !teacherCard &&
    (studentCard || groundTruthCard);
  // Show loading while we still expect teacher data that hasn't arrived yet.
  // Covers: evaluation still running ("evaluating"), LLM Judge phase
  // ("llm_as_a_judge"), and completed but teacher score not yet propagated.
  // Only exclude "failed" — there is no score coming.
  const evaluationNotFailed =
    evaluationResult?.distillation_status != null &&
    evaluationResult?.distillation_status !== 'failed';
  const studentOrBenchmarkHasScore =
    (studentCard?.score != null && studentCard?.score !== '') ||
    (groundTruthCard?.score != null && groundTruthCard?.score !== '');
  // The usecase config is the source of truth for whether a teacher is expected.
  // We also treat it as "expecting data" so the teacher loading indicator shows up
  // immediately after the processing overlay disappears, even before any teacher
  // event has reached the API (handles slow OpenAI Batch responses).
  const usecaseExpectsTeacher = Boolean(
    usecase?.comparison_mode &&
    ['teacher', 'all'].includes(usecase?.comparison_mode_option),
  );
  const teacherExpectingData =
    showComparison &&
    !teacherHasError &&
    !hasTeacherScore &&
    (
      teacherModelProcessing ||
      (teacherJudgeProgress && Number(teacherJudgeProgress.total) > 0) ||
      (evaluationNotFailed && studentOrBenchmarkHasScore) ||
      usecaseExpectsTeacher
    );
  const showTeacherCard = showComparison;
  const teacherActivelyLoading =
    !teacherHasError &&
    !loading &&
    !judgeLoading &&
    (teacherCardInProgress || teacherCardMissingButExpected || teacherExpectingData);

  const teacherModelDisplayName = teacherCard?.model_name || usecase?.teacher_model || 'Teacher';
  const teacherProgressCompleted = evaluationResult?.teacher_model_progress_completed;
  const teacherProgressTotal = evaluationResult?.teacher_model_progress_total;

  const teacherProgressText = (() => {
    if (!teacherActivelyLoading) return null;
    if (teacherModelProcessing) {
      const c = Number(teacherProgressCompleted) || 0;
      const t = Number(teacherProgressTotal) || 0;
      if (t > 0) return `${c} out of ${t} teacher responses completed`;
      return `Getting ${teacherModelDisplayName} response...`;
    }
    if (teacherJudgeProgress && Number(teacherJudgeProgress.total) > 0) {
      const c = Number(teacherJudgeProgress.completed) || 0;
      const t = Number(teacherJudgeProgress.total);
      const failed = Number(teacherJudgeProgress.failed) || 0;
      const suffix = failed > 0 ? ` (${failed} failed)` : '';
      return `${c} out of ${t} judge scores calculated${suffix}`;
    }
    if (evaluationNotFailed && studentOrBenchmarkHasScore) return `Loading ${teacherModelDisplayName} results...`;
    return `Preparing ${teacherModelDisplayName} evaluation...`;
  })();

  const teacherLoadingMessage = teacherProgressText;
  const teacherBarLoadingText = teacherProgressText;

  const teacherStartedAt = evaluationResult?.teacher_model_started_at || null;

  const overallScoresContent = (
    <OverallScoresRow
      groundTruthCard={groundTruthCard}
      studentCard={studentCard}
      teacherCard={teacherCard}
      teacherLoading={teacherActivelyLoading}
      teacherLoadingMessage={teacherLoadingMessage}
      showTeacher={showTeacherCard}
      teacherModelName={teacherModelDisplayName}
      teacherStartedAt={teacherStartedAt}
      storageKey={isFileComparison ? 'file_eval_start' : undefined}
    />
  );

  return (
    <div style={{ marginTop: 16 }}>
      {/* Charts */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {showContentGenerationSection && (
          <div style={{ width: '100%' }}>
            <ContentGenerationAccuracy
              data={contentGenerationData}
              teacherLabel={contentGenTeacherLabel}
              studentLabel={contentGenStudentLabel}
              showComparison={showComparison}
              loading={loading}
              teacherLoading={teacherActivelyLoading}
              onConfigure={() => setAccuracyModalOpen(true)}
            />
          </div>
        )}
        <div style={{ width: '100%' }}>
          <LLMJudgeBreakdownChart
            data={llmJudgeData}
            onConfigure={() => setJudgeModalOpen(true)}
            showTeacher={showComparison}
            loading={loading || judgeLoading}
            teacherLoading={teacherActivelyLoading}
            teacherBarLoadingText={teacherBarLoadingText}
            overallScoresContent={overallScoresContent}
          />
        </div>

        <div style={{ width: '100%', marginTop: 8 }}>
          <ClassicMetricsChart
            data={classicMetricsData}
            onConfigure={() => setClassicModalOpen(true)}
            showTeacher={showComparison}
            loading={loading}
            teacherLoading={teacherActivelyLoading}
            teacherBarLoadingText={teacherBarLoadingText}
          />
        </div>
      </div>

      <LLMJudgeConfigModal
        open={judgeModalOpen}
        onClose={() => setJudgeModalOpen(false)}
        value={{
          ...judgeConfig,
          builtins: builtinItems,
        }}
        onChange={(next) => {
          setJudgeConfig(next);
          const payload = judgeConfigToUsecaseUpdatePayload(next);
          onPersistJudgeConfig?.(payload);
        }}
      />

      <ClassicMetricsConfigModal
        open={classicModalOpen}
        onClose={() => setClassicModalOpen(false)}
        value={classicConfig}
        onChange={persistClassicConfig}
        scope="classic"
      />

      <ClassicMetricsConfigModal
        open={accuracyModalOpen}
        onClose={() => setAccuracyModalOpen(false)}
        value={classicConfig}
        onChange={persistClassicConfig}
        scope="accuracy"
      />
    </div>
  );
};

export default React.memo(SummarizationOverview);

